import React, { useContext, useEffect, useState } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  InputGroup,
  Row,
} from "react-bootstrap";
import { TableColumn } from "react-data-table-component";
import { FormProvider, useForm } from "react-hook-form";
import { BiSolidPencil } from "react-icons/bi";
import { useNavigate } from "react-router-dom";
import searchico from "../../assets/images/search.svg";
import { ESDataTable } from "../../controls/ESDataTable";
import ESSearchDropdown from "../../controls/ESSearchDropdown";
import ESTooltip from "../../controls/ESTooltip";
import { UserContext } from "../../routes/routes";
import ESAutocompleteSearch from "../../controls/ESAutocompleteSearch";
import {
  addTempelate,
  deleteTemplate,
  getAllOrganizationTemplates,
  getAllTemplates,
} from "../../utils/ApiUrl";
import { axiosPost } from "../../utils/AxiosHelper";
import {
  Option,
  PageRecordLimit,
  checkIfSuperAdmin,
  encrypt,
  formatDateCommon,
  getCachedData,
  setPageRecordLimit,
} from "../../utils/common";
import { ToastTypeCode, showToast } from "../../utils/Constant";
import ExportExcel from "../Common/ExportExcel/ExportExcel";
import Footer from "../footer/Footer";
import Header from "../header/Header";
import ConfirmationPopup from "../popUp/ConfirmationPopup";
import SubHeader from "../subheader/Subheader";
import AddTemplate from "./AddTempelate";
import "./TempelateList.scss";

interface UserTempelate {
  TempelateId: number;
  TempelateName: string;
  TempelateBody: string;
  TempelateSubject: string;
  IsDefault: boolean;
  OrganizationId: number;
  IsActive: boolean;
  CreateDate: string;
  InternalKey: string;
}

interface CustomTableColumn<T> extends TableColumn<T> {
  onClick?: () => void;
}

const TempelateList: React.FC = () => {
  const [userTemplates, setUserTemplates] = useState<UserTempelate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(PageRecordLimit);
  const [totalRecords, setTotalRecords] = useState(0);
  const [sortBy, setSortBy] = useState<string>("CreateDate");
  const [sortDir, setSortDir] = useState<string>("DESC");
  const [searchText, setSearchText] = useState<string>("");
  const [showPopup, setShowPopup] = useState(false);
  const [deleteId, setDeleteId] = useState<number>(0);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<number>(0);

  const handleFormClose = () => {
    setShowForm(false);
    fetchUserTemplates();
  };

  const methods = useForm();
  const [organizationId, setOrganizationId] = useState<number | 0>(0);
  const [organizations, setOrganizations] = useState<Option[]>([]);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const { appContextdata } = useContext<any>(UserContext);
  const [organizationDropDownChange, setOrganizationDropDownChange] =
    useState(false);
  const [defaultOraganizationDropDown, setDefaultOraganizationDropDown] =
    useState<Option | null>(null);

  const navigate = useNavigate();

  const fetchUserTemplates = async (
    organizationidFromProps?: number,
    searchOverride?: string,
  ) => {
    setLoading(true);
    setError(null);
    try {
      const response: unknown = await axiosPost(getAllOrganizationTemplates, {
        SearchSource: searchOverride !== undefined ? searchOverride : searchText,
        IsActive: true,
        CurrentPageNo: searchOverride !== undefined ? 1 : currentPage,
        PageSize: pageSize,
        SortDir: sortDir,
        SortBy: sortBy,
        OrganizationId: organizationidFromProps
          ? organizationidFromProps
          : !organizationDropDownChange && organizationId === 0
            ? -1
            : organizationId,
      });
      const data = (response as { data?: { ExtraData?: any } })?.data
        ?.ExtraData;
      setUserTemplates(data);
      setTotalRecords(data?.length > 0 ? data[0].TotalRecords : 0);
    } catch (error) {
      setError("Error fetching data");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async (id: number) => {
    let encryptedId = encodeURIComponent(await encrypt(id.toString()));
    navigate(`/tempelate/edit/${encryptedId}`);
  };

  const handleCreate = () => {
    navigate(addTempelate);
  };

  const handleDelete = async (id: number) => {
    if (id) {
      try {
        const response: unknown = await axiosPost(deleteTemplate, {
          TempelateId: id,
        });
        const responseData = (
          response as { data?: { Status?: number; ErrorMessage?: string } }
        )?.data;
        if (responseData?.Status === 200) {
          setUserTemplates((prevState) =>
            prevState.filter((item) => item.TempelateId !== id),
          );
          showToast("Template deleted successfully", ToastTypeCode?.SUCCESS);
        } else {
          showToast(`${responseData?.ErrorMessage}`, ToastTypeCode?.ERROR);
        }
        setShowPopup(false);
        fetchUserTemplates();
      } catch (error) {
        showToast("Error deleting template", ToastTypeCode?.ERROR);
      }
    }
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleSort = (column: any, sortDirection: "asc" | "desc") => {
    if (Object.keys(column)?.length === 0 && column.constructor === Object) {
      return;
    }
    setSortBy(column.sortField);
    setSortDir(sortDirection.toUpperCase());
  };

  const handleSearchTextChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setSearchText(event.target.value);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      handleSearch();
    }
  };

  const handleSearch = (searchOverride?: string) => {
    setCurrentPage(1);
    fetchUserTemplates(undefined, searchOverride);
  };

  const handlePerRowsChange = async (newPerPage: number, page: number) => {
    setPageSize(newPerPage);
  };

  const confirmDelete = (id: number) => {
    setDeleteId(id);
    setShowPopup(true);
  };

  useEffect(() => {
    if (appContextdata?.Organization) {
      fetchUserTemplates(appContextdata.Organization);
      setPageRecordLimit(pageSize);
    }
  }, [appContextdata?.Organization, currentPage, pageSize, sortBy, sortDir]);

  const handleOrganizationChange = (selectedOption: Option | null) => {
    setDefaultOraganizationDropDown(null);
    setOrganizationId(selectedOption?.value ?? 0);
    setCurrentPage(1);
    setOrganizationDropDownChange(true);
  };

  useEffect(() => {
    if (organizationDropDownChange) {
      fetchUserTemplates();
      setPageRecordLimit(pageSize);
    }
  }, [organizationId, currentPage, pageSize, sortBy, sortDir]);

  const fetchData = async () => {
    if (appContextdata?.Role) {
      const isAdmin = checkIfSuperAdmin(appContextdata);

      if (isAdmin) {
        setIsSuperAdmin(true);
        const options = (await getCachedData("organizations")) as Option[];
        setOrganizations(options);
        const defaultOption: any = options?.find(
          (option) => option.value === appContextdata?.Organization,
        );
        handleOrganizationChange(defaultOption);
        setDefaultOraganizationDropDown(defaultOption);
      }
    }
  };
  useEffect(() => {
    fetchData();
  }, [appContextdata?.Role]);

  const columns: CustomTableColumn<UserTempelate>[] = [
    {
      name: "Actions",
      cell: (row) => (
        <>
          <ESTooltip text="Edit Template" placement="top">
            <button
              onClick={() => handleEdit(row.TempelateId)}
              className="btnicon"
            >
              <BiSolidPencil
                color="#529c25"
                size={14}
                data-tour="edit-template"
              />
            </button>
          </ESTooltip>
        </>
      ),
      grow: 1,
      width: "80px",
    },
    {
      name: "Template Name",
      selector: (row) => row.TempelateName,
      sortable: true,
      sortField: "TempelateName",
      grow: 2,
    },
    {
      name: "Subject",
      selector: (row) => row.TempelateSubject,
      sortable: true,
      sortField: "TemplateSubject",
      grow: 4,
    },
    {
      name: "Default",
      cell: (row) => (
        <div className={row.IsDefault ? "isdefault" : "notdefault"}>
          {row.IsDefault ? "Yes" : "No"}
        </div>
      ),
      sortable: true,
      sortField: "IsDefault",
      grow: 1,
    },
    {
      name: "Last Modified",
      selector: (row) => formatDateCommon(row.CreateDate),
      sortable: true,
      sortField: "CreateDate",
      grow: 1,
    },
  ];

  return (
    <div className="wrap-usertemplate">
      <Header />
      <SubHeader />
      <div className="usertemplate-blk">
        <Container fluid>
          <Card>
            <CardHeader>
              <Row>
                <Col lg={6}>
                  <h2 className="page-title"> Email Templates</h2>
                </Col>
                <Col lg={6}>
                  <div className="search-usertemplate">
                    {isSuperAdmin && (
                      <FormProvider {...methods}>
                        {organizations && organizations?.length >= 1 && (
                          <ESSearchDropdown
                            name="OrganizationId"
                            placeholder="Select Organization"
                            options={organizations}
                            onChange={handleOrganizationChange}
                            defaultValue={defaultOraganizationDropDown}
                          />
                        )}
                      </FormProvider>
                    )}

                    <div style={{ flexGrow: 1, minWidth: "200px" }}>
                      <ESAutocompleteSearch
                        apiUrl={getAllOrganizationTemplates}
                        labelKey="TempelateName"
                        valueKey="TempelateId"
                        initialValue={searchText}
                        onSearchChange={(text) => setSearchText(text)}
                        onSelect={(item) => {
                          setSearchText(item.TempelateName);
                          handleSearch(item.TempelateName);
                        }}
                        onSubmit={() => handleSearch()}
                        searchKey="SearchSource"
                        otherParams={{
                          IsActive: true,
                          OrganizationId:
                            !organizationDropDownChange && organizationId === 0
                              ? -1
                              : organizationId,
                        }}
                      />
                    </div>
                    <div className="d-flex">
                      <ExportExcel
                        searchText={searchText}
                        model={userTemplates}
                        componentName={"TempelateList"}
                        fileName={"TempelateList"}
                      ></ExportExcel>
                      {/* {isSuperAdmin && (
                        <ESTooltip text="Add Template" placement="top">
                          <Button
                            onClick={() => handleCreate()}
                            className="primary-btn btn-add"
                          >
                            {showForm ? (
                              <img
                                src={crossico}
                                className="closeico"
                                alt="close"
                              />
                            ) : (
                              <img src={addico} alt="add" />
                            )}
                          </Button>
                        </ESTooltip>
                      )} */}
                    </div>
                  </div>
                </Col>
              </Row>
            </CardHeader>
            <CardBody>
              {showForm && (
                <AddTemplate tempelateId={editId} onClose={handleFormClose} />
              )}
              {error && <p>{error}</p>}
              <div className="table-responsive">
                <ESDataTable
                  columns={columns}
                  data={userTemplates}
                  paginationTotalRows={totalRecords}
                  onChangePage={handlePageChange}
                  onSort={handleSort}
                  onChangeRowsPerPage={handlePerRowsChange}
                />
              </div>
              {showPopup && (
                <ConfirmationPopup
                  show={showPopup}
                  message="Are you sure you want to delete this template?"
                  onConfirm={() => handleDelete(deleteId)}
                  onCancel={() => setShowPopup(false)}
                />
              )}
            </CardBody>
          </Card>
        </Container>
      </div>
      <Footer />
    </div>
  );
};

export default TempelateList;
